// Guided walkthrough definitions for the driver.js tours surfaced from the
// Header "Guided walkthroughs" menu. Each walkthrough is a list of driver.js
// steps anchored to element ids that exist on the relevant page.
//
// Anchors used on the find-covered-product page (Comparison Library / section 0):
//   #project-action          - project edit/delete menu
//   #comparison-library-tab  - Comparison Library tab
//   #source-section          - Source pane
//   #add-dropdown-source     - "Add" dropdown in the Source pane
//   #compare-with-section    - Compare With pane
//   #add-dropdown-compare    - "Add" dropdown in the Compare With pane
//   #vijilize-button         - Vijilize CTA
//   #vijilizations-tab       - Vijilizations tab
// Anchors used on the project list page:
//   #create-new-project      - Create New Project CTA

export interface WalkthroughStep {
  element: string;
  popover: {
    title: string;
    description: string;
    side?: "top" | "right" | "bottom" | "left";
    align?: "start" | "center" | "end";
  };
  /**
   * When true, the step's target element is programmatically clicked once it is
   * highlighted, so the tour can demonstrate the action live (e.g. clicking a
   * citation superscript opens the citation panel that a later step anchors to).
   */
  clickOnHighlight?: boolean;
  /**
   * When true, the heatmap "drill into first patent" action runs once this step
   * is highlighted, so the tour demonstrates a patent click expanding into the
   * claim-level heatmap live on the step (used by the heatmap walkthrough).
   */
  triggerDrillOnHighlight?: boolean;
  /**
   * When true, selects the first enabled examiner-citation reference when this
   * step is highlighted, so the "Next" button becomes enabled and the tour can
   * advance the modal to the vijilize step.
   */
  selectsReference?: boolean;
  /**
   * When true, clicking the tour's "Next" clicks the modal's own Next button to
   * advance it to the vijilize step, then moves the tour on once it re-renders.
   * Avoids auto-clicking on highlight, which removes the anchored button and
   * makes the highlight overlay spill.
   */
  advanceModalOnNext?: boolean;
}

export interface Walkthrough {
  /** Stable identifier. */
  id: string;
  /** Label shown in the menu. */
  title: string;
  /** driver.js steps. */
  steps: WalkthroughStep[];
  /**
   * When true, the menu handler ensures a source patent and at least one
   * target exist (opening the relevant Add modals) before starting the tour.
   */
  requiresVijilizeSetup?: boolean;
  /**
   * When true, the Compare With pane is switched to the URLs sub-tab before the
   * tour starts, so a step can anchor to the per-URL monitor icon.
   */
  activateUrlsTab?: boolean;
  /**
   * When true, the first covered-product target on the Vijilization detail page
   * is expanded before the tour starts, so steps can anchor to the "View More
   * Details" button and citation superscripts that only render once expanded.
   */
  expandsFirstTarget?: boolean;
  /**
   * When true, opens the View Examiner Citations modal on the first source
   * patent (that has citations) before the tour, so steps can anchor to
   * elements inside the modal.
   */
  opensExaminerCitations?: boolean;
}

// Anchors used on the Vijilization detail page (ClaimAnalysisVijilize):
//   .walkthrough-expand-target - per-target expand/collapse chevron
//   .walkthrough-view-more      - "View More Details" button in the analysis
//   .walkthrough-citation-sup   - citation superscript link in the analysis
//   #citation-panel             - the Citation side panel (opens on sup click)
// Each is a class so the driver.js step anchors to the first match on the page.

// Anchors used on the Vijilizations tab of the find-covered-product page:
//   #vijilizations-tab            - the Vijilizations tab
//   .walkthrough-vijilization-name - a run's name (click to open its analysis)
//   .vijilization-menu-container   - the per-row Action (⋮) menu

/** Walkthroughs available on the find-covered-product Vijilizations tab. */
export const VIJILIZATIONS_TAB_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "review-vijilizations-tab",
    title: "View Vijilizations",
    steps: [
      {
        element: "#vijilizations-tab",
        popover: {
          title: "Vijilizations",
          description:
            "Every analysis you run for this project is listed here, along with its status, who ran it, and when.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: ".walkthrough-vijilization-name",
        popover: {
          title: "Open a Vijilization",
          description:
            "Click a completed Vijilization's name to open it and view the detailed coverage analysis.",
          side: "right",
          align: "start",
        },
      },
      {
        element: ".vijilization-menu-container",
        popover: {
          title: "Edit or Delete",
          description:
            "Use this Action (⋮) menu on any row to edit a Vijilization's name and description, or to delete it.",
          side: "left",
          align: "start",
        },
      },
    ],
  },
];

/** Walkthroughs available on the Vijilization detail page. */
export const VIJILIZATION_DETAIL_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "read-vijilization",
    title: "Reading a Vijilization",
    expandsFirstTarget: true,
    steps: [
      {
        element: ".walkthrough-expand-target",
        popover: {
          title: "Expand a Covered Product",
          description:
            "Click this arrow to expand or collapse a covered product and reveal its matched and unmatched analysis against the selected claim.",
          side: "left",
          align: "start",
        },
      },
      {
        element: ".walkthrough-view-more",
        popover: {
          title: "View More Details",
          description:
            "Open the detailed analysis for this item, including the full source content and annotations.",
          side: "top",
          align: "start",
        },
      },
      {
        element: ".walkthrough-citation-sup",
        clickOnHighlight: true,
        popover: {
          title: "Open Citations",
          description:
            "Superscript numbers in the analysis are citations. Click one — like this — to open the reference panel and view the source it came from.",
          side: "left",
          align: "start",
        },
      },
      {
        element: "#citation-panel",
        popover: {
          title: "Citation Reference",
          description:
            "The Citation panel shows the source behind the selected superscript. Switch citations from the dropdown, and expand Specification, Prosecutional, or Precedential to read the referenced content.",
          side: "left",
          align: "start",
        },
      },
    ],
  },
];

// Anchors used on the Detailed Analysis (view-more) page (MarkdownViewer):
//   .edit-image-button        - "Annotation" button (edit annotations)
//   .reanalyze-button         - "ReAnalyze" button (re-run with new inputs)
//   .walkthrough-change-image - "Change Patent Image" button

/** Walkthroughs available on the Detailed Analysis (view-more) page. */
export const VIEW_MORE_DETAIL_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "detailed-analysis",
    title: "Working with the Analysis",
    steps: [
      {
        element: ".edit-image-button",
        popover: {
          title: "Annotation",
          description:
            "Open the annotation editor to mark up the source image — highlight regions and add notes that refine this element's analysis.",
          side: "left",
          align: "start",
        },
      },
      {
        element: ".reanalyze-button",
        popover: {
          title: "ReAnalyze",
          description:
            "Re-run the analysis for this element with new guidance — provide a fresh instruction or a new image to improve the result.",
          side: "left",
          align: "start",
        },
      },
      {
        element: ".walkthrough-change-image",
        popover: {
          title: "Change Patent Image",
          description:
            "Swap the patent figure used for this analysis. Pick a different image so the comparison reflects the right drawing.",
          side: "left",
          align: "start",
        },
      },
    ],
  },
];

// Steps for the "?" walkthrough inside the Change Patent/Vijilize Image modal.
//   #change-image-sample-tab - "Sample Images" tab
//   #change-image-upload-tab - "Upload Custom Image" tab
//   #change-image-submit     - "Change" button
export const CHANGE_IMAGE_WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    element: "#change-image-sample-tab",
    popover: {
      title: "Sample Images",
      description:
        "Browse the sample images detected for this patent and click one to select it.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: "#change-image-upload-tab",
    popover: {
      title: "Upload Your Own",
      description:
        "Or switch here to upload a custom image — SVG, PNG, or JPG up to 5 MB.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: "#change-image-submit",
    popover: {
      title: "Apply the Image",
      description:
        "Click Change to apply the selected image to this analysis.",
      side: "top",
      align: "end",
    },
  },
];

// Steps for the "?" walkthrough inside the ReAnalyze modal.
//   #reanalyze-instruction  - instruction textarea
//   #reanalyze-change-image - "Change Image" button
//   #reanalyze-submit       - "ReAnalyze" button
export const REANALYZE_WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    element: "#reanalyze-instruction",
    popover: {
      title: "Add an Instruction",
      description:
        "Optionally describe how the element should be re-analyzed — e.g. focus on a specific feature or correct a prior result.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: "#reanalyze-change-image",
    popover: {
      title: "Change the Image",
      description:
        "Optionally pick a different image to analyze against, using sample images or your own upload.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: "#reanalyze-submit",
    popover: {
      title: "Re-run the Analysis",
      description:
        "Click ReAnalyze to re-run this element with your new instruction and/or image.",
      side: "top",
      align: "end",
    },
  },
];

// Steps for the Annotation tool page walkthrough (surfaced both from the
// page's "?" button and the Header "Guided walkthroughs" menu).
//   #annotation-editor-area - the annotation canvas / editor
//   #annotation-actions     - the Update / Back actions
export const ANNOTATION_WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    element: "#annotation-editor-area",
    popover: {
      title: "Annotate the Image",
      description:
        "Click and drag to draw a box over a feature in the image, then type a label (suggestions appear) to annotate it. Use the Image Size slider to zoom.",
      side: "top",
      align: "center",
    },
  },
  {
    element: "#annotation-actions",
    popover: {
      title: "Save Your Annotations",
      description:
        "Once you've drawn your annotations, click Update Annotated Image to save them — or Back to return to the analysis.",
      side: "left",
      align: "start",
    },
  },
];

/** Walkthroughs available on the Annotation tool page (Header menu). */
export const ANNOTATION_PAGE_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "annotate-image",
    title: "Annotating the Image",
    steps: ANNOTATION_WALKTHROUGH_STEPS,
  },
];

// Anchors used on the Vijilize comparison heatmap page (ClaimAnalysisHeatmap):
//   #heatmap-chart-area - the heatmap chart wrapper (patent- and claim-level)
//   #heatmap-back-btn   - "Back to Patent Level Heatmap" (claim-level view only)
//   #heatmap-skip-btn   - "Skip Selection & Continue"
// Step 1 triggers the "drill into first patent" action on highlight, so the
// tour demonstrates a patent click expanding into the claim-level heatmap live;
// the claim boxes and the Back button then exist for the steps that follow.
export const HEATMAP_WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    element: ".apexcharts-yaxis",
    popover: {
      title: "Pick a Source Patent",
      description:
        "Your source patents are listed on the left — there may be more than one. Click a patent here to expand its claim-level heatmap.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "#heatmap-chart-area",
    popover: {
      title: "Claim-Level Heatmap",
      description:
        "We've opened the first patent for you. This is its claim-by-claim breakdown, with each box showing how strongly that claim is covered.",
      side: "top",
      align: "center",
    },
    triggerDrillOnHighlight: true,
  },
  {
    element: ".apexcharts-heatmap-rect",
    popover: {
      title: "Open a Claim's Vijilization",
      description:
        "Click any score box to jump straight to that claim's vijilization — you'll be taken to its detailed analysis for that compared product.",
      side: "top",
      align: "center",
    },
  },
  {
    element: "#heatmap-back-btn",
    popover: {
      title: "Back to Patent Level",
      description:
        "Use this to return to the patent-level heatmap and pick a different patent to drill into.",
      side: "bottom",
      align: "end",
    },
  },
  {
    element: "#heatmap-skip-btn",
    popover: {
      title: "Skip Selection & Continue",
      description:
        "Or skip selecting a claim and continue straight to the vijilization analysis.",
      side: "bottom",
      align: "end",
    },
  },
];

/** Walkthroughs available on the Vijilize comparison heatmap page (Header menu). */
export const HEATMAP_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "navigate-heatmap",
    title: "Navigating the Heatmap",
    steps: HEATMAP_WALKTHROUGH_STEPS,
  },
];

// Anchors used inside the Edit Vijilization modal:
//   #edit-vijilization-name        - Vijilization Name input
//   #edit-vijilization-description - Description textarea
//   #edit-vijilization-add-more    - "Add More & Re-Vijilize" button (completed runs only)
//   #edit-vijilization-cancel      - Cancel button
//   #edit-vijilization-update      - Update button

/**
 * Steps for the Edit Vijilization modal walkthrough, surfaced from the "?"
 * button in the modal header. The Add More & Re-Vijilize step only applies
 * when that button is present (completed runs), so it is appended
 * conditionally by the caller via {@link getEditVijilizationSteps}.
 */
const EDIT_VIJILIZATION_BASE_STEPS: WalkthroughStep[] = [
  {
    element: "#edit-vijilization-name",
    popover: {
      title: "Vijilization Name",
      description:
        "Rename this Vijilization. The name can't be empty — it's how you'll recognise the run in the Vijilizations list.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: "#edit-vijilization-description",
    popover: {
      title: "Description",
      description:
        "Add an optional description to capture context about this run, such as what you were comparing or why.",
      side: "bottom",
      align: "start",
    },
  },
];

const EDIT_VIJILIZATION_ADD_MORE_STEP: WalkthroughStep = {
  element: "#edit-vijilization-add-more",
  popover: {
    title: "Add More & Re-Vijilize",
    description:
      "Bring in additional sources or comparison targets and run the analysis again, building on this completed Vijilization.",
    side: "top",
    align: "start",
  },
};

const EDIT_VIJILIZATION_ACTION_STEPS: WalkthroughStep[] = [
  {
    element: "#edit-vijilization-cancel",
    popover: {
      title: "Cancel",
      description:
        "Discard your edits and close the dialog. The Vijilization keeps its current name and description.",
      side: "top",
      align: "start",
    },
  },
  {
    element: "#edit-vijilization-update",
    popover: {
      title: "Update",
      description:
        "Save your changes to the name and description. Enabled once the name isn't empty.",
      side: "top",
      align: "end",
    },
  },
];

/**
 * Build the Edit Vijilization walkthrough steps. The Add More & Re-Vijilize
 * step is included only when that button is shown (completed runs).
 */
export const getEditVijilizationSteps = (
  includeAddMore: boolean
): WalkthroughStep[] => [
  ...EDIT_VIJILIZATION_BASE_STEPS,
  ...(includeAddMore ? [EDIT_VIJILIZATION_ADD_MORE_STEP] : []),
  ...EDIT_VIJILIZATION_ACTION_STEPS,
];

/** Walkthroughs available on the project list page. */
export const PROJECT_LIST_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "create-project",
    title: "Create a Project",
    steps: [
      {
        element: "#create-new-project",
        popover: {
          title: "Create New Project!",
          description: "Create a new project to start the journey with us.",
        },
      },
    ],
  },
];

/** Walkthroughs available on the find-covered-product page (Comparison Library). */
export const FIND_COVERED_PRODUCT_WALKTHROUGHS: Walkthrough[] = [
  {
    id: "general",
    title: "General Walkthrough",
    steps: [
      {
        element: "#project-action",
        popover: {
          title: "Project Edit/Delete",
          description: "You can edit or delete the project.",
        },
      },
      {
        element: "#comparison-library-tab",
        popover: {
          title: "Comparison Library",
          description:
            "The Comparison Library contains your source material with the claims you want to compare.",
        },
      },
      {
        element: "#source-section",
        popover: {
          title: "Source Pane",
          description:
            "The Source pane has the patents you want to compare with. ",
        },
      },
      {
        element: "#add-dropdown-source",
        popover: {
          title: "Add Source",
          description:
            "You can add the Source Materials by clicking on Add button and select the appropriate type from the list.",
        },
      },
      {
        element: "#compare-with-section",
        popover: {
          title: "Compare With Pane",
          description:
            "The Compare With pane has the target patents, URL, PDF, and any other types of files or links to compare to.",
        },
      },
      {
        element: "#add-dropdown-compare",
        popover: {
          title: "Add Compare",
          description:
            "You can add the Compare Materials by clicking on Add button and select the appropriate type from the list.",
        },
      },
      {
        element: "#vijilize-button",
        popover: {
          title: "Vijilize",
          description:
            "Select at least one Source and Comparison to enable the Vijilize button.",
        },
      },
      {
        element: "#vijilizations-tab",
        popover: {
          title: "Vijilizations",
          description: "You can see the Vijilizations here.",
        },
      },
    ],
  },
  {
    // Mirrors the "How To run Vijilization against External Sources" manual:
    // select a source, select target patents/files/URLs, Vijilize, name the
    // run, then watch progress on the Vijilizations tab.
    id: "how-to-vijilize",
    title: "Run a Vijilization",
    requiresVijilizeSetup: true,
    steps: [
      {
        element: "#source-section",
        popover: {
          title: "Select a Source",
          description:
            "Tick the checkbox next to the source patent you want to compare from.",
        },
      },
      {
        element: "#compare-with-section",
        popover: {
          title: "Select Targets",
          description:
            "On the Patents tab, check the target patents to compare against — or switch to the Files or URLs tab to compare against a file or a product listing.",
        },
      },
      {
        element: "#add-dropdown-compare",
        popover: {
          title: "Add a Target",
          description:
            "Need something to compare against? Use Add to bring in a patent, file, URL, or patent number.",
        },
      },
      {
        element: "#vijilize-button",
        popover: {
          title: "Vijilize",
          description:
            "With a source and at least one target selected, click Vijilize. A dialog opens where you name the run, add an optional description, and pick claim options — then confirm.",
        },
      },
      {
        element: "#vijilizations-tab",
        popover: {
          title: "Track Progress",
          description:
            "Your run appears on the Vijilizations tab, where you can watch its progress and open completed results.",
        },
      },
    ],
  },
  {
    // Mirrors the "Configuring Product Monitoring Settings In IPVijil" manual:
    // per-source Examiner Citations and Similar Products monitoring (opened from
    // a source patent's ⋮ menu) and per-URL monitoring, each set via the
    // Monitor Item dialog (enable + frequency + save).
    id: "configure-monitoring",
    title: "Configure Product Monitoring",
    activateUrlsTab: true,
    steps: [
      {
        element: "#monitor-button",
        popover: {
          title: "Monitoring",
          description:
            "Monitoring re-checks your sources and targets on a schedule and alerts you to changes. The Monitor button shows this project's overall monitoring status.",
        },
      },
      {
        element: "#source-section",
        popover: {
          title: "Monitor Examiner Citations",
          description:
            "Open the ⋮ menu on a source patent and choose “View Examiner Citations”. Click Monitor, turn on Enable monitoring, choose a frequency, and Save to track new examiner citations.",
        },
      },
      {
        element: "#source-section",
        popover: {
          title: "Monitor Similar Products",
          description:
            "From the same ⋮ menu choose “View Similar Products”. Click Monitor, enable it, and pick a frequency (Daily, Weekly, or Monthly) to be alerted when new similar products appear.",
        },
      },
      {
        element: ".walkthrough-url-monitor",
        popover: {
          title: "Monitor URLs",
          description:
            "On the URLs tab, click this monitor icon on a URL row. Enable monitoring and choose a frequency to track changes to that product listing.",
        },
      },
    ],
  },
  {
    id: "manage-source-patents",
    title: "Manage Source Patents",
    steps: [
      {
        element: "#comparison-library-tab",
        popover: {
          title: "Comparison Library",
          description:
            "Sources and comparison targets are managed here in the Comparison Library.",
        },
      },
      {
        element: "#source-section",
        popover: {
          title: "Source Pane",
          description:
            "The Source pane holds the patents you want to analyze. Select them with the checkboxes.",
        },
      },
      {
        element: "#add-dropdown-source",
        popover: {
          title: "Add a Source",
          description:
            "Click Add to bring in a source by patent number or by uploading a file.",
        },
      },
    ],
  },
  {
    id: "add-comparison-targets",
    title: "Add Comparison Targets",
    steps: [
      {
        element: "#compare-with-section",
        popover: {
          title: "Compare With Pane",
          description:
            "The Compare With pane holds your target patents, URLs, PDFs, and other files.",
        },
      },
      {
        element: "#add-dropdown-compare",
        popover: {
          title: "Add a Target",
          description:
            "Click Add to bring in a comparison patent, file, URL, or patent number.",
        },
      },
      {
        element: "#vijilize-button",
        popover: {
          title: "Ready to Vijilize",
          description:
            "Once a source and at least one target are selected, click Vijilize to compare them.",
        },
      },
    ],
  },
  {
    id: "review-vijilizations",
    title: "Review Vijilizations",
    steps: [
      {
        element: "#vijilizations-tab",
        popover: {
          title: "Vijilizations",
          description:
            "Switch to the Vijilizations tab to see every completed analysis for this project.",
        },
      },
      {
        element: "#comparison-library-tab",
        popover: {
          title: "Back to Library",
          description:
            "Return to the Comparison Library anytime to set up a new analysis.",
        },
      },
    ],
  },
  {
    // Walks through comparing a patent's prosecution history: open its examiner
    // citations, browse citation lists, pick a reference, add/monitor, then
    // vijilize. Opens the View Examiner Citations modal first (opensExaminerCitations).
    id: "compare-prosecution-history",
    title: "Compare Prosecution History",
    opensExaminerCitations: true,
    steps: [
      {
        element: ".walkthrough-citation-lists",
        popover: {
          title: "Citation Lists",
          description:
            "The examiner's references are grouped by type — Non-Final Rejection, Final Rejection, and references cited by the examiner.<br/><br/>Click a group to switch between them.",
          side: "right",
          align: "start",
        },
      },
      {
        element: ".walkthrough-citation-reference",
        popover: {
          title: "Pick a Reference",
          description:
            "Tick a reference you want to work with — we've selected one for you. Selected references can be added to your project or vijilized.",
          side: "right",
          align: "start",
        },
        selectsReference: true,
      },
      {
        element: ".walkthrough-add-to-project",
        popover: {
          title: "Add to Project",
          description:
            "Add the selected references to your project's Compare With pane so you can reuse them in future vijilizations.",
          side: "top",
        },
      },
      {
        element: ".walkthrough-monitor",
        popover: {
          title: "Monitor",
          description:
            "Turn on monitoring to get alerted when new examiner citations appear for this patent.",
          side: "left",
        },
      },
      {
        element: ".walkthrough-next-vijilize",
        popover: {
          title: "Begin Vijilizing",
          description:
            "Click Next to move on and set up the vijilization for the selected references.",
          side: "top",
        },
        advanceModalOnNext: true,
      },
      {
        element: ".walkthrough-vijilize-content",
        popover: {
          title: "Name Your Run",
          description:
            "Give the vijilization a name and an optional description so you can find it later on the Vijilizations tab.",
          side: "left",
        },
      },
      {
        element: ".walkthrough-claim-options",
        popover: {
          title: "Choose Claims",
          description:
            "Pick 'Use only Independent Claims' to analyze just the independent claims, or 'Select Claims' to choose specific claims from the claim graph.",
          side: "right",
        },
      },
      {
        element: ".walkthrough-vijilize-submit",
        popover: {
          title: "Vijilize",
          description:
            "Once the name and claim options look right, click Vijilize to start the analysis.<br/><br/>You'll find the run on the Vijilizations tab.",
          side: "top",
        },
      },
    ],
  },
];
